import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import type { FileMenuActionEnum } from '../../enums/FileMenuActionEnum';
import type { KeyboardShortcutId } from '../../lib/keyboardShortcuts/types';

export type KeyboardShortcutHandler = () => void;

export interface FilesKeyboardNavigationHandlers {
  onArrow: (direction: 'up' | 'down' | 'left' | 'right', extend: boolean) => void;
  onMoveFocus: (direction: 'up' | 'down' | 'left' | 'right') => void;
  onToggleSelection: () => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onToggleGridList: () => void;
  onFocusLandmark: (landmark: 'sidebar' | 'main' | 'details' | 'upload') => void;
}

interface KeyboardShortcutsContextValue {
  isEnabled: boolean;
  registerHandler: (id: KeyboardShortcutId, handler: KeyboardShortcutHandler) => void;
  unregisterHandler: (id: KeyboardShortcutId, handler: KeyboardShortcutHandler) => void;
  invokeHandler: (id: KeyboardShortcutId) => void;
  registerFocusSearch: (handler: KeyboardShortcutHandler | null) => void;
  invokeFocusSearch: () => void;
  registerOpenShortcutsModal: (handler: KeyboardShortcutHandler | null) => void;
  invokeOpenShortcutsModal: () => void;
  registerFileMenuAction: (handler: ((action: FileMenuActionEnum) => void) | null) => void;
  invokeFileMenuAction: (action: FileMenuActionEnum) => void;
  registerFilesNavigation: (handlers: FilesKeyboardNavigationHandlers | null) => void;
  getFilesNavigation: () => FilesKeyboardNavigationHandlers | null;
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextValue | null>(null);

export function KeyboardShortcutsProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const handlersRef = useRef(new Map<KeyboardShortcutId, Set<KeyboardShortcutHandler>>());
  const focusSearchRef = useRef<KeyboardShortcutHandler | null>(null);
  const openShortcutsModalRef = useRef<KeyboardShortcutHandler | null>(null);
  const fileMenuActionRef = useRef<((action: FileMenuActionEnum) => void) | null>(null);
  const filesNavigationRef = useRef<FilesKeyboardNavigationHandlers | null>(null);

  const isViewerRoute = /^\/viewer\//.test(location.pathname);
  const isEnabled = !isViewerRoute;

  const registerHandler = useCallback(
    (id: KeyboardShortcutId, handler: KeyboardShortcutHandler) => {
      const bucket = handlersRef.current.get(id) ?? new Set();
      bucket.add(handler);
      handlersRef.current.set(id, bucket);
    },
    [],
  );

  const unregisterHandler = useCallback(
    (id: KeyboardShortcutId, handler: KeyboardShortcutHandler) => {
      handlersRef.current.get(id)?.delete(handler);
    },
    [],
  );

  const invokeHandler = useCallback((id: KeyboardShortcutId) => {
    handlersRef.current.get(id)?.forEach((handler) => handler());
  }, []);

  const registerFocusSearch = useCallback((handler: KeyboardShortcutHandler | null) => {
    focusSearchRef.current = handler;
  }, []);

  const invokeFocusSearch = useCallback(() => {
    focusSearchRef.current?.();
  }, []);

  const registerOpenShortcutsModal = useCallback((handler: KeyboardShortcutHandler | null) => {
    openShortcutsModalRef.current = handler;
  }, []);

  const invokeOpenShortcutsModal = useCallback(() => {
    openShortcutsModalRef.current?.();
  }, []);

  const registerFileMenuAction = useCallback(
    (handler: ((action: FileMenuActionEnum) => void) | null) => {
      fileMenuActionRef.current = handler;
    },
    [],
  );

  const invokeFileMenuAction = useCallback((action: FileMenuActionEnum) => {
    fileMenuActionRef.current?.(action);
  }, []);

  const registerFilesNavigation = useCallback(
    (handlers: FilesKeyboardNavigationHandlers | null) => {
      filesNavigationRef.current = handlers;
    },
    [],
  );

  const getFilesNavigation = useCallback(() => filesNavigationRef.current, []);

  const value = useMemo(
    (): KeyboardShortcutsContextValue => ({
      getFilesNavigation,
      invokeFileMenuAction,
      invokeFocusSearch,
      invokeHandler,
      invokeOpenShortcutsModal,
      isEnabled,
      registerFileMenuAction,
      registerFilesNavigation,
      registerFocusSearch,
      registerHandler,
      registerOpenShortcutsModal,
      unregisterHandler,
    }),
    [
      getFilesNavigation,
      invokeFileMenuAction,
      invokeFocusSearch,
      invokeHandler,
      invokeOpenShortcutsModal,
      isEnabled,
      registerFileMenuAction,
      registerFilesNavigation,
      registerFocusSearch,
      registerHandler,
      registerOpenShortcutsModal,
      unregisterHandler,
    ],
  );

  return (
    <KeyboardShortcutsContext.Provider value={value}>{children}</KeyboardShortcutsContext.Provider>
  );
}

export function useKeyboardShortcutsContext(): KeyboardShortcutsContextValue {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error('useKeyboardShortcutsContext must be used within KeyboardShortcutsProvider');
  }
  return context;
}

export function useKeyboardShortcutsContextOptional(): KeyboardShortcutsContextValue | null {
  return useContext(KeyboardShortcutsContext);
}
